import PropTypes from 'prop-types';
import React from 'react';

import ExperimentMwebAppInstallBanner from '@wetransfer/transfer/constant/experiments/mweb-app-install-banner';
import { isPaidTransfer } from '@wetransfer/transfer/helper/paidTransfers';
import Browser from '@wetransfer/transfer/modules/concorde-browser';
import Store from '@wetransfer/transfer/store';
import { TRANSFER_STATES } from '@wetransfer/transfer/transfer-bundle/constant/transfer-states';
import {
  prepareTransferDownload,
  fetchTransferDownloadDetails,
} from '@wetransfer/transfer/transfer-bundle/store/download-actions';

import AppInstallBanner from './AppInstallBanner/AppInstallBanner';
import Blocked from './blocked';
import Complete from './Complete';
import DownloadableTransfer from './DownloadableTransfer';
import Expired from './expired';
import FilesContainer from './FilesContainer';
import { Invalid } from './Invalid';
import Password from './password';
import Progress from './progress';
import Spam from './spam';

// If the transfer is processing, we'll poll for updates.
const POLL_INTERVAL = 2500;

export default class Downloader extends React.Component {
  static propTypes = {
    id: PropTypes.string.isRequired,
    secret: PropTypes.string.isRequired,
    recipient_id: PropTypes.string,
    file_id: PropTypes.string,
    transferWindowRef: PropTypes.object,
  };

  /**
   * Binds all private methods
   * @param  {Object} props Component props
   */
  constructor(props) {
    super(props);

    this.adsContainer = true;
    this.pollTransferDetails = this.pollTransferDetails.bind(this);
  }

  // When this component gets mounted, we'll see if there is already sideloaded information
  // available to speed up the process.
  componentDidMount() {
    // By default we'll have an empty transfer that is processing.
    let transferDetails = {
      state: TRANSFER_STATES.PROCESSING,
    };

    if (Browser.isMobile) {
      this.panelContainer = document.querySelector('.panel.panel--full');
      this.panelContainer?.classList.add('mobile-panel-receiver-UI');

      this.adsContainer = document.querySelector('.mobile-web-micro-frontend');

      if (!this.adsContainer) {
        this.panelContainer?.classList.add(
          'mobile-panel-receiver-UI--no-ad-container'
        );
      }
    }

    // Get the sideloaded Transfer information and remove the global object.
    if (
      window['_preloaded_transfer_'] &&
      typeof window['_preloaded_transfer_'] === 'object'
    ) {
      transferDetails = window['_preloaded_transfer_'];
      window['_preloaded_transfer_'] = null;
    }

    // Create a new Transfer object with the sideloaded data.
    Store.dispatch(prepareTransferDownload(transferDetails));

    // Get the transfer from our store
    const { transfer } = Store.getState();

    // No longer processing, done or error
    if (
      transfer.state !== TRANSFER_STATES.PROCESSING &&
      transfer.state !== TRANSFER_STATES.PASSWORD_PROTECTED
    ) {
      return;
    }

    // Load Transfer Details from the API if it's still processing
    this.pollTransferDetails();
  }

  // When this component gets unmounted, we can stop the polling timer if it's active
  componentWillUnmount() {
    if (Browser.isMobile) {
      this.panelContainer = document.querySelector('.panel.panel--full');
      this.panelContainer?.classList.remove('mobile-panel-receiver-UI');

      if (!this.adsContainer) {
        this.panelContainer?.classList.remove(
          'mobile-panel-receiver-UI--no-ad-container'
        );
      }
    }

    clearTimeout(this.timer);
  }

  componentDidUpdate() {
    // Stop timer if the state is already downloadable
    const { transfer } = Store.getState();

    if (transfer.state === TRANSFER_STATES.DOWNLOADABLE) {
      clearTimeout(this.timer);
    }
  }

  // Load Transfer details for our current transfer
  pollTransferDetails() {
    const { transfer, download } = Store.getState();

    if (
      transfer.state === TRANSFER_STATES.PASSWORD_PROTECTED &&
      !transfer.password
    ) {
      // Try again after the INTERVAL
      clearTimeout(this.timer);
      this.timer = setTimeout(this.pollTransferDetails, POLL_INTERVAL);

      return;
    }

    // If the current transfer state is not processing, we don't have to poll
    if (transfer.state !== TRANSFER_STATES.PROCESSING) {
      clearTimeout(this.timer);

      return;
    }

    // Only send a new request if we're not still waiting for one.
    if (!download.isPending) {
      // Load the Transfer data from our API
      const password = transfer.password || false;

      Store.dispatch(
        fetchTransferDownloadDetails({
          id: this.props.id,
          secret: this.props.secret,
          recipientId: this.props.recipient_id,
          password,
        })
      );
    }

    // Try again after the INTERVAL
    clearTimeout(this.timer);
    this.timer = setTimeout(this.pollTransferDetails, POLL_INTERVAL);
  }

  render() {
    const { transfer } = Store.getState();
    let component = null;

    if (!transfer.state) {
      return false;
    }

    switch (transfer.state) {
      // Transfer is still in processing.
      case TRANSFER_STATES.PROCESSING:
      case TRANSFER_STATES.INIT:
        component = <FilesContainer />;
        break;

      // The transfer is no longer available for download
      case TRANSFER_STATES.EXPIRED:
        component = <Expired transfer={transfer} />;
        break;

      // This is not a valid transfer (404 I guess)
      case TRANSFER_STATES.INVALID:
        component = <Invalid transfer={transfer} />;
        break;

      // This transfer was reported as spam
      case TRANSFER_STATES.SPAM:
        component = <Spam transfer={transfer} />;
        break;

      case TRANSFER_STATES.PASSWORD_PROTECTED:
        component = (
          <Password
            transferWindowRef={this.props.transferWindowRef}
            transferId={transfer.id}
            secret={this.props.secret}
            recipient={this.props.recipient_id}
          />
        );
        break;

      // The transfer is downloadable, show a summary of files
      case TRANSFER_STATES.DOWNLOADABLE:
        // TODO: Add a condition to check whether the Transfer is paid or not
        // before we release to the Alpha group of users.
        // https://wetransfer.atlassian.net/browse/PUB-539
        component = isPaidTransfer(transfer.paid) ? (
          <DownloadableTransfer />
        ) : (
          <FilesContainer isReady />
        );
        break;

      // Download has started.
      case TRANSFER_STATES.DOWNLOADING:
        // You started the download
        component = <Progress />;
        break;

      // Download finished
      case TRANSFER_STATES.FINISHED:
        // The download has started in the browsers but is finished from our point of view
        component = <Complete />;
        break;

      case TRANSFER_STATES.BLOCKED:
        component = <Blocked />;
        break;
    }

    /**
     * We show the app download banner on mobile (iOS and Android)
     * - If we are not running the mweb install banner experiment
     * - As the control variant if we are running the mweb install banner experiment
     */
    let shouldDisplayAppInstallBanner =
      ExperimentMwebAppInstallBanner.isReceiverInfoView() &&
      transfer.state === TRANSFER_STATES.DOWNLOADABLE &&
      !isPaidTransfer(transfer.paid);

    let snowplowProperty = 'download_page';
    let amplitudeContext = 'Mobile Web: Download Page';

    if (
      ExperimentMwebAppInstallBanner.isDownloadStartedView() &&
      transfer.state === TRANSFER_STATES.FINISHED &&
      !isPaidTransfer(transfer.paid)
    ) {
      shouldDisplayAppInstallBanner = true;
      snowplowProperty = 'download_started';
      amplitudeContext = 'Mobile Web: Download Started';
    }

    return (
      <div>
        {component}
        {shouldDisplayAppInstallBanner && (
          <AppInstallBanner
            id={this.props.id}
            secret={this.props.secret}
            snowplowProperty={snowplowProperty}
            amplitudeContext={amplitudeContext}
            view="receive"
            context="download"
          />
        )}
      </div>
    );
  }
}
